/**
 * CitizenStatusLookupPage — `/government/citizen-lookup`.
 *
 * Staff-facing citizen lookup. Backed by `POST /v1/government/lookup` which
 * supports lookup by NIN (matched against the SHA-256 identity-ref hash
 * already persisted on every request), phone (matched against inputData.phone),
 * customer id, or request number.
 */
import { type JSX, useState } from 'react';
import { Info, Search } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  lookupCitizen,
  type CitizenLookupResponse,
} from '../../lib/api/government';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

type LookupKind = 'nin' | 'phone' | 'customerId' | 'requestNumber';

const KIND_LABELS: Record<LookupKind, string> = {
  nin: 'NIN (11 digits)',
  phone: 'Phone',
  customerId: 'Customer ID (UUID)',
  requestNumber: 'Request number',
};

export function CitizenStatusLookupPage(): JSX.Element {
  const [kind, setKind] = useState<LookupKind>('nin');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CitizenLookupResponse | null>(null);

  const lookup = useMutation({
    mutationFn: () => lookupCitizen({ [kind]: value.trim() }),
    onSuccess: (res) => {
      setResults(res);
      setError(null);
    },
    onError: (e) => {
      setResults(null);
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setError('Permission required: Government/lookupCitizen');
      } else if (e instanceof ApiError && e.status === 400) {
        setError(`Validation failed for ${kind}.`);
      } else {
        setError((e as Error).message);
      }
    },
  });

  function placeholderFor(k: LookupKind): string {
    switch (k) {
      case 'nin': return '11-digit NIN';
      case 'phone': return '+234…';
      case 'customerId': return 'UUID';
      case 'requestNumber': return 'SR-2026-…';
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Citizen status lookup" description="Look up a citizen's government-service requests by NIN, phone, customer id, or request number." />

      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          Lookups are audit-logged with NDPR basis <code className="font-mono">NDPR_LEGITIMATE_INTEREST</code>.
          NIN matches use the SHA-256 hash already on each request — cleartext NIN is never persisted.
        </p>
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Search by</label>
            <select
              data-testid="lookup-kind"
              value={kind}
              onChange={(e) => { setKind(e.target.value as LookupKind); setValue(''); setResults(null); }}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              {(Object.keys(KIND_LABELS) as LookupKind[]).map((k) => (
                <option key={k} value={k}>{KIND_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-muted mb-1">{KIND_LABELS[kind]}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  data-testid="lookup-value"
                  type="text"
                  value={value}
                  onChange={(e) => setValue(kind === 'nin' ? e.target.value.replace(/\D/g, '').slice(0, 11) : e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && value.trim().length > 0 ? lookup.mutate() : null}
                  placeholder={placeholderFor(kind)}
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm font-mono"
                />
              </div>
              <button
                type="button"
                data-testid="lookup-submit"
                onClick={() => lookup.mutate()}
                disabled={lookup.isPending || value.trim().length === 0}
                className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-nipost-green-dark"
              >
                {lookup.isPending ? 'Looking up…' : 'Look up'}
              </button>
            </div>
          </div>
        </div>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>

      {results ? (
        results.items.length === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No service requests for that {KIND_LABELS[kind].toLowerCase()}.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-2 border-b border-border bg-gray-50 text-xs text-muted">
              Matched {results.total} request(s) by <code className="font-mono">{results.matchedBy}</code>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Request #</th>
                  <th className="text-left px-4 py-2">Service</th>
                  <th className="text-left px-4 py-2">Agency</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {results.items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.requestNumber}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.serviceCode}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.agency}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(r.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
